import { ReactNode } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxWidth?: number;
  showCloseButton?: boolean;
}

const Modal = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 700,
  showCloseButton = true,
}: ModalProps) => {
  const theme = useTheme();

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={onClose}
      />

      {/* Modal Content */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 99999,
          backgroundColor: '#fff',
          borderRadius: '8px',
          maxWidth: maxWidth,
          maxHeight: '85vh',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <Box
            sx={{
              padding: '20px 24px',
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {title && (
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                {title}
              </Typography>
            )}
            {showCloseButton && (
              <IconButton
                onClick={onClose}
                sx={{
                  ml: 'auto',
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        )}

        {/* Body */}
        <Box
          sx={{
            padding: '24px',
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </Box>
    </>,
    document.body
  );
};

export default Modal;
